/**
 * PATH: apps/quote/src/pages/SuppliersPage.tsx
 * PURPOSE: Supplier management - connected to backend APIs
 * 
 * FLOW:
 *   ┌─────────────┐    ┌─────────────┐    ┌─────────────┐
 *   │ Fetch       │───▶│ Search/     │───▶│ Display     │
 *   │ Suppliers   │    │ Filter      │    │ Grid        │
 *   └─────────────┘    └─────────────┘    └─────────────┘
 * 
 * DEPENDENCIES:
 *   - quoteApi: API client for backend communication
 *   - @tanstack/react-query: Data fetching and caching
 */

import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, Building2, Loader2, Globe, Package, AlertCircle, RefreshCw } from 'lucide-react';
import { quoteApi, type Supplier } from '@/lib/quoteApi';
import { cn } from '@/lib/utils';

export default function SuppliersPage() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch suppliers from API
  const { 
    data: suppliers = [], 
    isLoading,
    error,
  } = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => quoteApi.listSuppliers(),
    retry: 1,
  });

  // Filter suppliers based on search
  const filteredSuppliers = useMemo(() => {
    if (!searchQuery.trim()) return suppliers;
    
    const query = searchQuery.toLowerCase();
    return suppliers.filter((supplier) =>
      supplier.name?.toLowerCase().includes(query) ||
      supplier.code?.toLowerCase().includes(query) ||
      supplier.specialty?.toLowerCase().includes(query)
    );
  }, [suppliers, searchQuery]);

  // Loading state
  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Suppliers</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Manage supplier information</p>
          </div>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
            <div>
              <h3 className="font-medium text-red-800 dark:text-red-200">Failed to load suppliers</h3>
              <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                {error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.'}
              </p>
            </div>
          </div>
          <button 
            onClick={() => queryClient.invalidateQueries({ queryKey: ['suppliers'] })}
            className="mt-3 px-4 py-2 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded-md hover:bg-red-200 dark:hover:bg-red-900/60 transition-colors flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" /> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Suppliers</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {suppliers.length} suppliers
          </p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          <Plus className="h-4 w-4" />
          Add Supplier
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search suppliers by name, code, or specialty..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        />
      </div>

      {/* Empty State */}
      {filteredSuppliers.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-8 text-center">
          <Building2 className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {suppliers.length === 0 ? 'No suppliers yet' : 'No matching suppliers'}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {suppliers.length === 0 
              ? 'Add your first supplier to get started.'
              : 'Try a different search term.'}
          </p>
        </div>
      )}

      {/* Suppliers Grid */}
      {filteredSuppliers.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSuppliers.map((supplier) => (
            <SupplierCard key={supplier.id} supplier={supplier} />
          ))}
        </div>
      )}
    </div>
  );
}

interface SupplierCardProps {
  supplier: Supplier;
}

function SupplierCard({ supplier }: SupplierCardProps) {
  // Generate a color based on supplier name
  const colorIndex = supplier.name?.charCodeAt(0) % 6 || 0;
  const colors = [
    'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300',
    'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300',
    'bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-300',
    'bg-orange-100 dark:bg-orange-900 text-orange-600 dark:text-orange-300',
    'bg-cyan-100 dark:bg-cyan-900 text-cyan-600 dark:text-cyan-300',
    'bg-pink-100 dark:bg-pink-900 text-pink-600 dark:text-pink-300',
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4 mb-4">
        <div className={cn(
          "h-12 w-12 rounded-lg flex items-center justify-center flex-shrink-0",
          colors[colorIndex]
        )}>
          <Building2 className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 dark:text-white truncate">
            {supplier.name}
          </h3>
          {supplier.code && (
            <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">
              {supplier.code}
            </p>
          )}
        </div>
        {supplier.is_active === false && (
          <span className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded">
            Inactive
          </span>
        )}
      </div>

      {supplier.specialty && (
        <div className="flex items-center gap-2 mb-3">
          <Package className="h-4 w-4 text-gray-400 flex-shrink-0" />
          <span className="text-sm text-gray-600 dark:text-gray-400 truncate">
            {supplier.specialty}
          </span>
        </div>
      )}

      {supplier.website && (
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-gray-400 flex-shrink-0" />
          <a 
            href={supplier.website.startsWith('http') ? supplier.website : `https://${supplier.website}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:underline truncate"
          >
            {supplier.website.replace(/^https?:\/\//, '')}
          </a>
        </div>
      )}

      {/* Future: Add product count when API supports it */}
      {/* <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700 mt-4">
        <div className="text-sm">
          <span className="text-gray-500 dark:text-gray-400">Products: </span>
          <span className="font-medium text-gray-900 dark:text-white">--</span>
        </div>
      </div> */}
    </div>
  );
}
